import {
  FileListResponse,
  FilePresignResponse,
  FileRow,
  FileTypeInfo,
  PresignResult,
  ScanFile,
} from '../../app/modules/types/files'
import { apiClient } from '../client'

/**
 * File upload API endpoints — the presign/confirm hops of the three-call
 * upload flow (see src/app/pages/scan/components/ScanFileUpload.tsx for the
 * middle hop, a bare PUT to a presigned URL that must NOT go through
 * apiClient).
 */
export const filesApi = {
  /**
   * Ask for a presigned upload URL, sending the file's SHA-256 (signed into the
   * URL). Rejects bad extension / oversize here, before any bytes are sent.
   *
   * 201 is a new upload (`{ upload }` — PUT, then confirm). 200 means this
   * organization already uploaded these bytes as this type: the body is the
   * existing `ScanFile` (`{ existing }`) and the PUT and confirm are skipped.
   * The HTTP status is the only signal, as with `confirm`.
   */
  presign: async (filename: string, size: number, sha256: string): Promise<PresignResult> => {
    const response = await apiClient.post<FilePresignResponse | ScanFile>('/files/presign/', {
      filename,
      size,
      sha256,
    })
    return response.status === 200
      ? {existing: response.data as ScanFile}
      : {upload: response.data as FilePresignResponse}
  },

  /**
   * Confirm a completed upload. The checksum was signed at presign, so this is
   * a single HEAD on the object and returns quickly for any size.
   * Returns 201 on a fresh file, or 200 when the same bytes were confirmed by
   * another upload in the meantime (dedup) — same body shape either way, so the HTTP status is the
   * only signal; surfaced here as `deduped` for the caller.
   */
  confirm: async (uploadId: string): Promise<{ file: ScanFile; deduped: boolean }> => {
    const response = await apiClient.post<ScanFile>(`/files/${uploadId}/confirm/`, {})
    return { file: response.data, deduped: response.status === 200 }
  },

  /** The org's uploaded files, with grade and severity counts per row. */
  getList: async (params?: string): Promise<FileListResponse> => {
    const { data } = await apiClient.get<FileListResponse>(params ? `/files/?${params}` : '/files/')
    return data
  },

  /** One file — the same shape as a list row. A 404 also covers another organization's id. */
  getById: async (id: string): Promise<FileRow> => {
    const { data } = await apiClient.get<FileRow>(`/files/${encodeURIComponent(id)}/`)
    return data
  },

  /** The file types this deployment accepts — extensions, content type, size cap. */
  types: async (): Promise<FileTypeInfo[]> => {
    const { data } = await apiClient.get('/files/types/')
    return data
  },
}
